import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Download, 
  Edit, 
  Play, 
  Trash2, 
  Settings,
  Smartphone,
  Globe,
  Palette,
  Upload,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react'
import { AppConfig, Build } from '../types'
import { mockApiCalls } from '../utils/mockData'

export function AppDetail() {
  const { appId } = useParams<{ appId: string }>()
  const navigate = useNavigate()
  const [app, setApp] = useState<AppConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedBuild, setSelectedBuild] = useState<Build | null>(null)
  const [showLogs, setShowLogs] = useState(false)

  useEffect(() => {
    const loadApp = async () => {
      if (!appId) return
      
      try {
        const appData = await mockApiCalls.getAppById(appId)
        setApp(appData)
        if (appData && appData.builds.length > 0) {
          setSelectedBuild(appData.builds[0])
        }
      } catch (error) {
        console.error('Failed to load app:', error)
      } finally {
        setLoading(false)
      }
    }

    loadApp()
  }, [appId])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-success-500" />
      case 'failed':
        return <XCircle className="w-5 h-5 text-error-500" />
      case 'building':
      case 'queued':
        return <Clock className="w-5 h-5 text-brand-500 animate-pulse" />
      default:
        return <AlertTriangle className="w-5 h-5 text-warning-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
      case 'completed':
        return 'text-success-600 bg-success-50 border-success-200'
      case 'failed':
        return 'text-error-600 bg-error-50 border-error-200'
      case 'building':
      case 'queued':
        return 'text-brand-600 bg-brand-50 border-brand-200'
      default:
        return 'text-warning-600 bg-warning-50 border-warning-200'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="flex items-center space-x-4">
          <div className="w-8 h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="h-64 bg-gray-200 rounded-xl"></div>
            <div className="h-96 bg-gray-200 rounded-xl"></div>
          </div>
          <div className="h-96 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    )
  }

  if (!app) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">App not found</h3>
        <p className="mt-1 text-sm text-gray-500">The app you're looking for doesn't exist.</p>
        <div className="mt-4">
          <Link to="/history" className="btn-primary">
            Back to Apps
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/history')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-lg font-medium"
            style={{ backgroundColor: app.branding.primaryColor }}
          >
            {app.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{app.name}</h1>
            <p className="text-gray-600">{app.description}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <Link
            to={`/apps/${app.id}/edit`}
            className="btn-outline flex items-center space-x-2"
          >
            <Edit className="w-4 h-4" />
            <span>Edit</span>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Configuration</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(app.status)}`}>
                {getStatusIcon(app.status)}
                <span className="ml-2 capitalize">{app.status}</span>
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Smartphone className="w-4 h-4 mr-2" />
                    App Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Package ID:</span>
                      <span className="text-gray-900 font-mono">{app.packageName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Version:</span>
                      <span className="text-gray-900">{app.version}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Company:</span>
                      <span className="text-gray-900">{app.branding.companyName}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Palette className="w-4 h-4 mr-2" />
                    Branding
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Primary Color:</span>
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: app.branding.primaryColor }}
                        ></div>
                        <span className="text-sm font-mono text-gray-900">
                          {app.branding.primaryColor}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Platforms</h3>
            <div className="space-y-3">
              {app.platforms.map((platform) => (
                <div key={platform} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Smartphone className={`w-5 h-5 ${
                      platform === 'ios' ? 'text-gray-700' : 'text-green-600'
                    }`} />
                    <span className="font-medium text-gray-900 capitalize">
                      {platform === 'ios' ? 'iOS' : 'Android'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 